import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api, { formatINR } from '@/lib/api';
import { useCart } from '@/context/CartContext';
import { useSettings } from '@/context/SettingsContext';
import { CHECKOUT } from '@/constants/testIds';
import { ShieldCheck, Wallet, QrCode, X, CheckCircle } from '@phosphor-icons/react';

const INDIAN_STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat',
  'Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra',
  'Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim',
  'Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal',
  'Delhi','Jammu & Kashmir','Ladakh','Chandigarh','Puducherry','Andaman & Nicobar Islands','Dadra & Nagar Haveli','Daman & Diu','Lakshadweep',
];

export default function Checkout() {
  const navigate = useNavigate();
  const { items, subtotal, clear } = useCart();
  const { settings } = useSettings();

  const threshold = settings.free_delivery_above || 299;
  const minOrder = settings.min_order_value || 199;
  const deliveryCharge = subtotal >= threshold || subtotal === 0 ? 0 : (settings.delivery_charge || 30);
  const platformFeeCfg = Number(settings.platform_fee) || 0;
  const platformFee = subtotal > 0 ? platformFeeCfg : 0;
  const total = subtotal + deliveryCharge + platformFee;
  const serviceablePincodes = new Set((settings.serviceable_pincodes || []).map((p) => String(p).trim()));
  const serviceAreaLabel = settings.service_area_label || 'our service area';

  const [form, setForm] = useState({
    name: '', phone: '', email: '', line1: '', line2: '',
    city: '', state: '', pincode: '', landmark: '', notes: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('COD'); // 'COD' | 'UPI'
  const [showUpiPopup, setShowUpiPopup] = useState(false);
  const upiAvailable = Boolean((settings.upi_id || '').trim());

  const pincodeStatus = (() => {
    const p = form.pincode.trim();
    if (!/^\d{6}$/.test(p)) return null;
    if (serviceablePincodes.size === 0) return 'ok';
    return serviceablePincodes.has(p) ? 'ok' : 'out_of_area';
  })();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!/^\d{10}$/.test(form.phone)) e.phone = 'Enter 10-digit phone';
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) e.email = 'Invalid email';
    if (!form.line1.trim()) e.line1 = 'Required';
    if (!form.city.trim()) e.city = 'Required';
    if (!form.state.trim()) e.state = 'Select state';
    if (!/^\d{6}$/.test(form.pincode)) e.pincode = 'Enter 6-digit PIN';
    else if (serviceablePincodes.size > 0 && !serviceablePincodes.has(form.pincode.trim())) {
      e.pincode = `We deliver only in ${serviceAreaLabel} right now`;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  if (items.length === 0) {
    return <div className="max-w-2xl mx-auto px-4 py-16 text-center">
      <h1 className="font-heading text-3xl text-stone-900">Your cart is empty</h1>
      <button onClick={() => navigate('/shop')} className="mt-4 inline-flex h-11 px-6 items-center rounded-full bg-terracotta-600 text-white font-medium">Continue shopping</button>
    </div>;
  }

  const placeOrder = async (e) => {
    e.preventDefault();
    if (subtotal < minOrder) {
      toast.error(`Minimum order value is ${formatINR(minOrder)}`);
      return;
    }
    if (!validate()) {
      toast.error('Please fix the form errors');
      return;
    }
    // UPI flow: show payment popup first; actual API call happens on confirm.
    if (paymentMethod === 'UPI' && !showUpiPopup) {
      setShowUpiPopup(true);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post('/orders', {
        items: items.map((it) => ({ product_id: it.id, quantity: it.quantity })),
        address: {
          name: form.name.trim(), phone: form.phone.trim(),
          line1: form.line1.trim(), line2: form.line2.trim(),
          city: form.city.trim(), state: form.state, pincode: form.pincode.trim(),
          landmark: form.landmark.trim(),
        },
        customer_email: form.email.trim(),
        notes: form.notes.trim(),
        payment_method: paymentMethod,
      });
      clear();
      try { localStorage.setItem('zk_last_order', JSON.stringify({ order_no: data.order_no, total: data.total, phone: form.phone.trim(), placed_at: data.created_at || new Date().toISOString() })); } catch (e) {}
      toast.success(`Order ${data.order_no} placed!`);
      setShowUpiPopup(false);
      navigate(`/order-success/${data.order_no}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not place order');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = (k) => `w-full h-11 px-4 rounded-xl bg-white border ${errors[k] ? 'border-red-400' : 'border-stone-200'} focus:border-olive-400 focus:outline-none text-sm`;

  return (
    <div className="fade-in max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <h1 className="font-heading text-3xl sm:text-4xl text-stone-900 mb-6">Checkout</h1>

      <form onSubmit={placeOrder} data-testid={CHECKOUT.form} className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-3xl p-5 sm:p-6 border border-stone-200">
            <h2 className="font-heading text-lg text-stone-900 mb-4">Delivery Address</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-stone-600">Full name *</label>
                <input data-testid={CHECKOUT.name} value={form.name} onChange={set('name')} className={inputCls('name')} placeholder="Aapka naam"/>
                {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600">Phone *</label>
                <input data-testid={CHECKOUT.phone} value={form.phone} onChange={set('phone')} className={inputCls('phone')} placeholder="10-digit mobile" maxLength={10}/>
                {errors.phone && <p className="text-xs text-red-600 mt-1">{errors.phone}</p>}
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-medium text-stone-600">Email (optional)</label>
                <input data-testid={CHECKOUT.email} value={form.email} onChange={set('email')} className={inputCls('email')} placeholder="you@example.com"/>
                {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-medium text-stone-600">Address line 1 *</label>
                <input data-testid={CHECKOUT.line1} value={form.line1} onChange={set('line1')} className={inputCls('line1')} placeholder="Flat / House / Street"/>
                {errors.line1 && <p className="text-xs text-red-600 mt-1">{errors.line1}</p>}
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-medium text-stone-600">Address line 2</label>
                <input data-testid={CHECKOUT.line2} value={form.line2} onChange={set('line2')} className={inputCls('line2')} placeholder="Area / Locality"/>
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600">City *</label>
                <input data-testid={CHECKOUT.city} value={form.city} onChange={set('city')} className={inputCls('city')} placeholder="City"/>
                {errors.city && <p className="text-xs text-red-600 mt-1">{errors.city}</p>}
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600">State *</label>
                <select data-testid={CHECKOUT.state} value={form.state} onChange={set('state')} className={inputCls('state')}>
                  <option value="">Select state</option>
                  {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                {errors.state && <p className="text-xs text-red-600 mt-1">{errors.state}</p>}
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600">PIN code *</label>
                <input data-testid={CHECKOUT.pincode} value={form.pincode} onChange={set('pincode')} className={inputCls('pincode')} placeholder="6-digit PIN" maxLength={6}/>
                {errors.pincode && <p className="text-xs text-red-600 mt-1">{errors.pincode}</p>}
                {!errors.pincode && pincodeStatus === 'ok' && (
                  <p data-testid="checkout-pincode-serviceable" className="text-xs text-olive-700 mt-1">✓ We deliver to this PIN code.</p>
                )}
                {!errors.pincode && pincodeStatus === 'out_of_area' && (
                  <p data-testid="checkout-pincode-not-serviceable" className="text-xs text-terracotta-700 mt-1">
                    Out of service area. We currently deliver only in {serviceAreaLabel}.
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600">Landmark</label>
                <input data-testid={CHECKOUT.landmark} value={form.landmark} onChange={set('landmark')} className={inputCls('landmark')} placeholder="Near..."/>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-medium text-stone-600">Order notes</label>
                <textarea data-testid={CHECKOUT.notes} value={form.notes} onChange={set('notes')} rows={2} className="w-full px-4 py-2 rounded-xl bg-white border border-stone-200 focus:border-olive-400 focus:outline-none text-sm" placeholder="Any delivery instructions..."/>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-5 sm:p-6 border border-stone-200">
            <h2 className="font-heading text-lg text-stone-900 mb-3">Payment</h2>
            <div className="space-y-3">
              <label
                data-testid="payment-method-cod"
                className={`flex items-center gap-3 p-4 rounded-2xl border-2 cursor-pointer transition-colors ${paymentMethod === 'COD' ? 'border-terracotta-600 bg-terracotta-50' : 'border-stone-200 hover:border-stone-300'}`}
              >
                <input type="radio" name="pm" value="COD" checked={paymentMethod === 'COD'} onChange={() => setPaymentMethod('COD')} className="sr-only"/>
                <Wallet size={28} weight="duotone" className="text-terracotta-700"/>
                <div className="flex-1">
                  <p className="font-semibold text-stone-900">Cash on Delivery (COD)</p>
                  <p className="text-xs text-stone-600">Pay in cash when your order arrives.</p>
                </div>
                {paymentMethod === 'COD' && <CheckCircle size={22} weight="fill" className="text-terracotta-600"/>}
              </label>

              {upiAvailable && (
                <label
                  data-testid="payment-method-upi"
                  className={`flex items-center gap-3 p-4 rounded-2xl border-2 cursor-pointer transition-colors ${paymentMethod === 'UPI' ? 'border-olive-600 bg-olive-50' : 'border-stone-200 hover:border-stone-300'}`}
                >
                  <input type="radio" name="pm" value="UPI" checked={paymentMethod === 'UPI'} onChange={() => setPaymentMethod('UPI')} className="sr-only"/>
                  <QrCode size={28} weight="duotone" className="text-olive-700"/>
                  <div className="flex-1">
                    <p className="font-semibold text-stone-900">Pay with UPI</p>
                    <p className="text-xs text-stone-600">Scan QR or pay to {settings.upi_id} after confirming.</p>
                  </div>
                  {paymentMethod === 'UPI' && <CheckCircle size={22} weight="fill" className="text-olive-700"/>}
                </label>
              )}
              <div className="flex items-center gap-1.5 text-xs text-stone-500">
                <ShieldCheck size={14} weight="duotone" className="text-olive-700"/> Secure checkout
              </div>
            </div>
          </div>
        </div>

        <aside className="lg:sticky lg:top-24 self-start space-y-3 bg-white rounded-3xl p-5 sm:p-6 border border-stone-200">
          <h3 className="font-heading text-lg text-stone-900">Order Summary</h3>
          <div className="max-h-56 overflow-y-auto space-y-2 pr-1">
            {items.map((it) => (
              <div key={it.id} className="flex items-center gap-3 text-sm">
                <img src={it.image_url || '/placeholder.png'} alt={it.name} className="w-12 h-12 rounded-lg object-cover bg-stone-100"/>
                <div className="flex-1 min-w-0"><p className="text-stone-900 line-clamp-1">{it.name}</p><p className="text-xs text-stone-500">x{it.quantity}</p></div>
                <p className="text-stone-900 font-medium">{formatINR(it.price * it.quantity)}</p>
              </div>
            ))}
          </div>
          <div className="border-t border-stone-200 pt-3 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-stone-500">Subtotal</span><span className="font-medium">{formatINR(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-stone-500">Delivery</span><span className="font-medium">{deliveryCharge === 0 ? <span className="text-olive-700">FREE</span> : formatINR(deliveryCharge)}</span></div>
            {platformFee > 0 && (
              <div className="flex justify-between" data-testid="checkout-platform-fee"><span className="text-stone-500">Platform fee</span><span className="font-medium">{formatINR(platformFee)}</span></div>
            )}
            <div className="flex justify-between items-baseline pt-2 border-t border-stone-100">
              <span className="font-heading text-base">Total</span>
              <span className="font-heading text-xl">{formatINR(total)}</span>
            </div>
          </div>
          {subtotal < minOrder && (
            <p className="text-xs text-terracotta-700 bg-terracotta-50 px-3 py-2 rounded-lg">Minimum order value is {formatINR(minOrder)}.</p>
          )}
          {pincodeStatus === 'out_of_area' && (
            <p className="text-xs text-terracotta-700 bg-terracotta-50 px-3 py-2 rounded-lg">We deliver only in {serviceAreaLabel} for now. Please enter a serviceable PIN code.</p>
          )}
          <button
            type="submit"
            disabled={submitting || subtotal < minOrder || pincodeStatus === 'out_of_area'}
            data-testid={CHECKOUT.placeOrderBtn}
            className="w-full h-12 rounded-full bg-terracotta-600 hover:bg-terracotta-700 text-white font-semibold text-base disabled:bg-stone-300 transition-colors"
          >
            {submitting ? 'Placing order…' : `Place Order · ${formatINR(total)}`}
          </button>
          <p className="text-[11px] text-stone-500 text-center">By placing your order, you agree to our <a className="underline" href="/terms">terms</a> & <a className="underline" href="/privacy">privacy policy</a>.</p>
        </aside>
      </form>

      {showUpiPopup && (
        <div
          data-testid="upi-payment-popup"
          className="fixed inset-0 z-[90] bg-stone-900/70 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => !submitting && setShowUpiPopup(false)}
        >
          <div
            className="bg-white rounded-3xl p-6 sm:p-7 max-w-md w-full shadow-2xl relative max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              data-testid="upi-popup-close"
              onClick={() => !submitting && setShowUpiPopup(false)}
              aria-label="Close"
              disabled={submitting}
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-stone-100 hover:bg-stone-200 grid place-items-center disabled:opacity-50"
            >
              <X size={18} weight="bold"/>
            </button>
            <div className="text-center space-y-3">
              <QrCode size={32} weight="duotone" className="text-olive-700 mx-auto"/>
              <h3 className="font-heading text-xl text-stone-900">Pay with UPI</h3>
              <p className="text-sm text-stone-600">
                Amount: <span className="font-bold text-stone-900">{formatINR(total)}</span>
              </p>
              {settings.upi_qr_url ? (
                <div className="bg-cream-50 border border-stone-200 rounded-2xl p-4 grid place-items-center">
                  <img
                    data-testid="upi-popup-qr"
                    src={settings.upi_qr_url}
                    alt="UPI QR"
                    className="w-full max-w-[260px] h-auto"
                  />
                </div>
              ) : (
                <div className="bg-cream-50 border border-stone-200 rounded-2xl p-6 text-xs text-stone-500">
                  (No QR image uploaded yet. Please pay to the UPI ID below.)
                </div>
              )}
              <div className="bg-olive-50 border border-olive-200 rounded-xl px-3 py-2.5">
                <p className="text-[11px] uppercase tracking-wide text-olive-700">UPI ID</p>
                <p data-testid="upi-popup-upi-id" className="font-mono font-semibold text-stone-900 text-base select-all">{settings.upi_id}</p>
                <button
                  type="button"
                  onClick={() => { navigator.clipboard?.writeText(settings.upi_id); toast.success('UPI ID copied'); }}
                  className="mt-1 text-xs text-olive-700 font-semibold hover:underline"
                >
                  Copy UPI ID
                </button>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 text-left">
                <b>How to pay:</b>
                <ol className="list-decimal ml-4 mt-1 space-y-0.5">
                  <li>Open your UPI app (GPay / PhonePe / Paytm)</li>
                  <li>Scan the QR or send to the UPI ID above</li>
                  <li>Pay exactly {formatINR(total)}</li>
                  <li>Tap "I've paid" below — admin will verify &amp; confirm</li>
                </ol>
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => !submitting && setShowUpiPopup(false)}
                  disabled={submitting}
                  className="flex-1 h-11 rounded-full border border-stone-300 text-stone-700 text-sm font-semibold hover:bg-stone-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-testid="upi-popup-confirm"
                  onClick={() => placeOrder({ preventDefault: () => {} })}
                  disabled={submitting}
                  className="flex-1 h-11 rounded-full bg-olive-700 text-white text-sm font-semibold hover:bg-olive-800 disabled:bg-stone-300"
                >
                  {submitting ? 'Placing…' : "I've paid · Place order"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
